import { View } from 'tamagui';

import { Text } from '@/components/text';
import { deriveAvatarIdentity } from '@/lib/avatar-identity';
import { borderWidth } from '@/theme/tokens.generated';

// Nearkin never shows a real photo on a thread/post card -- per
// design.md, a real photo only unlocks after mutual reveal, which
// doesn't exist yet in this slice. Until then, every author gets a
// small pseudonymous stand-in: a colored circle carrying one letter,
// in the same concentric-circle "stamped seal" family as the brand
// mark (components/logo-mark.tsx) and the verification screen's
// CaptureBadge (screens/verification-pending/index.tsx) -- this app
// already has a visual language for "a person's identity is a round
// badge," so this reuses that shape rather than inventing a new one.
//
// The actual color/initial derivation is pure and lives in
// lib/avatar-identity.ts, not here -- see that file's own comment for
// why (a jest limitation on importing 'tamagui', not a style choice).
//
// Reused by ThreadCard (screens/threads) and PostCard/ThreadHeader
// (screens/thread-detail) -- one shared component rather than three
// copies of the same circle-and-letter markup.
export function PseudonymousAvatar({ handle, id, size = 32 }: { handle?: string | null; id: string; size?: number }) {
  const identity = deriveAvatarIdentity(handle, id);
  return (
    <View
      width={size}
      height={size}
      borderRadius="$full"
      backgroundColor={identity.bg}
      borderWidth={borderWidth.hairline}
      // $borderColorStrong, not the dedicated $avatarBorder token: tried
      // $avatarBorder first (a plain white ring, #ffffff in BOTH themes
      // -- tokens.generated.ts) and RENDERED it -- on the neutral
      // palette slot the ring measured ~1.15:1 against the card (both
      // are near-white creams), so that avatar nearly disappeared into
      // its own card. $borderColorStrong (this app's existing
      // "essential control boundary" token, already used for the
      // card's own border/shadow -- see cardRecipe in
      // screens/threads/index.tsx) measures, worst case, 3.39:1 (dark,
      // vs card) and 4.05:1 (light, vs the stamp palette fill) -- every
      // combination of page/card background and palette fill checked
      // clears 3:1, so every avatar gets a real visible edge regardless
      // of which of the five colors it hashed into (final review,
      // PR #52: inlined here since the original measurement table lived
      // only in a gitignored tool-scratch report, .superpowers/sdd/,
      // not anywhere durable).
      borderColor="$borderColorStrong"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      // Purely decorative/redundant with the handle text rendered right
      // next to it at every call site, so it's hidden from screen
      // readers rather than announced twice -- accessibilityElementsHidden
      // (iOS/Android) and importantForAccessibility (Android; React
      // Native Web maps both to the DOM equivalent) cover native and web.
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      <Text color={identity.text} fontWeight="$semibold" fontSize={Math.round(size * 0.42)} selectable={false}>
        {identity.initial}
      </Text>
    </View>
  );
}
